// src/components/repository/RepositorySettings.js
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Tab } from '@headlessui/react'
import { Switch } from '@headlessui/react'
import { supabase } from '../../utils/supabase'
import Layout from '../common/Layout'
import Button from '../common/Button'
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// 基础设置组件
function GeneralSettings({ settings, onUpdate }) {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Apply EchoGuard to:</h3>
                <div className="mt-4 space-y-4">
                    <div className="flex items-center">
                        <Switch
                            checked={settings?.apply_to_issues ?? true}
                            onChange={(checked) => onUpdate({ ...settings, apply_to_issues: checked })}
                            className={`${
                                settings?.apply_to_issues ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                        >
                            <span
                                className={`${
                                    settings?.apply_to_issues ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                        </Switch>
                        <span className="ml-3 text-sm font-medium text-gray-900">Issues</span>
                    </div>

                    <div className="flex items-center">
                        <Switch
                            checked={settings?.apply_to_discussions ?? false}
                            onChange={(checked) => onUpdate({ ...settings, apply_to_discussions: checked })}
                            className={`${
                                settings?.apply_to_discussions ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                        >
                            <span
                                className={`${
                                    settings?.apply_to_discussions ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                        </Switch>
                        <span className="ml-3 text-sm font-medium text-gray-900">Discussions</span>
                    </div>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                    Select where EchoGuard will monitor and take actions.
                </p>
            </div>
        </div>
    )
}

// 核心功能：相似问题检测
function CoreFeatures({ settings, onUpdate, repository, plan }) {
    return (
        <div className="space-y-6">
            <div className="flex items-start space-x-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                    <MagnifyingGlassIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                    <h3 className="text-xl font-semibold text-gray-900"> Similar Issue Detection</h3>
                    <p className="text-sm text-gray-600 mt-1">Find and link related issues automatically</p>
                </div>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Action on Detection</label>
                    <select
                        value={settings?.similar_issue_action || 'comment_with_links'}
                        onChange={(e) => onUpdate({ ...settings, similar_issue_action: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                    >
                        <option value="comment_with_links">Post a comment with links to similar issues</option>
                        <option value="add_label_and_comment">Add 'potential-duplicate' label and comment</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Duplicate Label</label>
                    <input
                        type="text"
                        value={settings?.similar_issue_label || 'potential-duplicate'}
                        onChange={(e) => onUpdate({ ...settings, similar_issue_label: e.target.value })}
                        className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                        placeholder="potential-duplicate"
                    />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-base font-medium text-blue-900 mb-2">Usage & Plan</h4>
                    <div className="space-y-2 text-sm">
                        <p><strong>Current Plan:</strong> {plan?.name || 'Free'}</p>
                        <p><strong>Monthly Checks:</strong> {plan?.id === 'free' ? 100 : 500}</p>
                        <p><strong>Results limit:</strong> {plan?.id === 'free' ? 'Maximum of 3' : 'Unlimited'}</p>
                    </div>
                    {plan?.id === 'free' && (
                        <div className="mt-3">
                            <Button variant="primary" size="sm">
                                Unlock unlimited checks & all results
                            </Button>
                        </div>
                    )}
                </div>

                {/*{plan?.id !== 'free' && (*/}
                {/*    <div className="border-t border-gray-200 pt-6">*/}
                {/*        <h4 className="text-base font-medium text-gray-900 mb-4">Bulk Similarity Check</h4>*/}
                {/*        <p className="text-sm text-gray-600 mb-4">*/}
                {/*            Run similarity check on all existing issues. This will consume a large number of detection credits.*/}
                {/*        </p>*/}
                {/*        <Button variant="secondary">*/}
                {/*            Run check on all existing issues*/}
                {/*        </Button>*/}
                {/*    </div>*/}
                {/*)}*/}
            </div>
        </div>
    )
}

// 附加自动化功能
function AdditionalAutomations({ settings, onUpdate }) {
    return (
        <div className="space-y-8">
            <div>
                <h3 className="text-lg font-medium text-gray-700 mb-6">Additional Automations</h3>

                {/* Auto-Title Generation */}
                <div className="space-y-4 mb-8">
                    <div className="flex items-start space-x-3">
                        <SparklesIcon className="w-5 h-5 text-purple-600 mt-0.5" />
                        <div>
                            <h4 className="text-base font-medium text-gray-900">Auto-Title Generation</h4>
                            <p className="text-sm text-gray-600 mt-1">
                                EchoGuard will automatically analyze content and generate clear, consistent titles,
                                directly replacing the original title when needed.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Spam Detection */}
                <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                            <h4 className="text-base font-medium text-gray-900">Spam Detection</h4>
                            <p className="text-sm text-gray-600 mt-1">Automatically detect and handle spam content</p>
                        </div>
                    </div>

                    <div className="ml-8 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Action on Spam Detection</label>
                            <select
                                value={settings?.spam_check_action || 'add_label_and_comment'}
                                onChange={(e) => onUpdate({ ...settings, spam_check_action: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                            >
                                <option value="add_label_and_close">Add 'spam' label and close</option>
                                <option value="add_label_only">Add 'spam' label only</option>
                                <option value="comment_and_close">Post a warning comment and close</option>
                                <option value="add_label_and_comment">Add 'spam' label and post a warning comment</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Spam Label</label>
                            <input
                                type="text"
                                value={settings?.spam_check_label || 'spam'}
                                onChange={(e) => onUpdate({ ...settings, spam_check_label: e.target.value })}
                                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                                placeholder="spam"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// 重新组织的主要功能设置组件
function AllFeatureSettings({ settings, onUpdate, repository, plan }) {
    return (
        <div className="space-y-8">
            {/* 基础设置 */}
            <div className="border-b border-gray-200 pb-8">
                <GeneralSettings settings={settings} onUpdate={onUpdate} />
            </div>

            {/* 核心功能：相似问题检测 */}
            <div className="border-b border-gray-200 pb-8">
                <CoreFeatures
                    settings={settings}
                    onUpdate={onUpdate}
                    repository={repository}
                    plan={plan}
                />
            </div>

            {/* 附加自动化功能 */}
            <div>
                <AdditionalAutomations settings={settings} onUpdate={onUpdate} />
            </div>
        </div>
    )
}

function ActivityLog({ activities, loading }) {
    const [filter, setFilter] = useState('all')

    const filteredLogs = filter === 'all'
        ? activities
        : activities.filter(log => log.type.toLowerCase().includes(filter))

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Activity Log</h3>
                <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2"
                >
                    <option value="all">All Activities</option>
                    <option value="spam">Spam Check</option>
                    <option value="title">Title Generated</option>
                    <option value="similar">Similar Issues</option>
                </select>
            </div>

            <div className="space-y-4">
                {filteredLogs.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircleIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-lg">No activity logs found</p>
                        <p className="text-gray-400 text-sm mt-1">Activity will appear here when EchoGuard processes issues</p>
                    </div>
                ) : (
                    filteredLogs.map((log) => {
                        let Icon, color;
                        switch (log.type) {
                            case 'spam_check':
                                Icon = ExclamationTriangleIcon;
                                color = 'red';
                                break;
                            case 'auto_title':
                                Icon = SparklesIcon;
                                color = 'purple';
                                break;
                            case 'similar_issue':
                                Icon = MagnifyingGlassIcon;
                                color = 'green';
                                break;
                            default:
                                Icon = CheckCircleIcon;
                                color = 'blue';
                        }

                        return (
                            <div key={log.id} className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200 border border-gray-100">
                                <div className={`p-3 rounded-xl ${
                                    color === 'red' ? 'bg-red-100' :
                                        color === 'purple' ? 'bg-purple-100' :
                                            color === 'green' ? 'bg-green-100' :
                                                'bg-blue-100'
                                }`}>
                                    <Icon className={`w-5 h-5 ${
                                        color === 'red' ? 'text-red-600' :
                                            color === 'purple' ? 'text-purple-600' :
                                                color === 'green' ? 'text-green-600' :
                                                    'text-blue-600'
                                    }`} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-base font-medium text-gray-900">{log.log_message}</p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {new Date(log.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}

function FeedbackInbox() {
    return (
        <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m12 0V9.5a2.5 2.5 0 00-2.5-2.5h-3A2.5 2.5 0 008 9.5V13" />
                </svg>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Coming Soon: Unified Feedback Inbox
            </h3>

            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                Tired of tracking user feedback from emails, community forums, or databases? EchoGuard will soon launch a new feature to help you consolidate all feedback channels in one place, intelligently analyze them with AI, and generate structured GitHub Issues with one click.
            </p>

            <Button variant="primary" size="lg">
                Join the Waitlist
            </Button>
        </div>
    )
}

export default function RepositorySettings() {
    const { id } = useParams()
    const [repository, setRepository] = useState(null)
    const [plan, setPlan] = useState(null)
    const [isEnabled, setIsEnabled] = useState(false)
    const [settings, setSettings] = useState({})
    const [activities, setActivities] = useState([])
    const [loading, setLoading] = useState(true)
    const [activitiesLoading, setActivitiesLoading] = useState(true)
    const [error, setError] = useState(null)

    // Fetch repository data
    const fetchRepositoryData = async () => {
        try {
            setLoading(true)

            // Fetch repository with plan info
            const { data: repoData, error: repoError } = await supabase
                .from('repositories')
                .select(`
         *,
         plans (*)
       `)
                .eq('id', id)
                .single()

            if (repoError) throw repoError

            if (!repoData) {
                setError('Repository not found')
                return
            }

            setRepository(repoData)
            setPlan(repoData.plans)
            setIsEnabled(repoData.is_active)

            // Fetch repository settings
            const { data: settingsData, error: settingsError } = await supabase
                .from('repository_settings')
                .select('*')
                .eq('repo_id', id)
                .single()

            if (settingsError && settingsError.code !== 'PGRST116') {
                // PGRST116 is "not found" error, which is OK for new repos
                throw settingsError
            }

            setSettings(settingsData || {})

        } catch (error) {
            console.error('Error fetching repository data:', error)
            setError('Failed to load repository data')
        } finally {
            setLoading(false)
        }
    }

    // Fetch activity logs
    const fetchActivityLogs = async () => {
        try {
            setActivitiesLoading(true)

            const { data: logsData, error: logsError } = await supabase
                .from('activity_logs')
                .select('*')
                .eq('repo_id', id)
                .order('created_at', { ascending: false })
                .limit(50)

            if (logsError) throw logsError

            setActivities(logsData || [])

        } catch (error) {
            console.error('Error fetching activity logs:', error)
        } finally {
            setActivitiesLoading(false)
        }
    }

    // Update repository active status
    const handleToggleEnabled = async (enabled) => {
        try {
            const { error } = await supabase
                .from('repositories')
                .update({ is_active: enabled })
                .eq('id', id)

            if (error) throw error

            setIsEnabled(enabled)
        } catch (error) {
            console.error('Error updating repository status:', error)
        }
    }

    // Update settings
    const updateSettings = async (newSettings) => {
        try {
            // Upsert settings
            const { error } = await supabase
                .from('repository_settings')
                .upsert({
                    repo_id: parseInt(id),
                    ...newSettings,
                    id: undefined
                }, {
                    onConflict: 'repo_id'
                })

            if (error) throw error

            setSettings(prev => ({
                ...prev,
                ...newSettings
            }))

        } catch (error) {
            console.error('Error updating settings:', error)
        }
    }

    useEffect(() => {
        if (id) {
            fetchRepositoryData()
            fetchActivityLogs()
        }
    }, [id])

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        )
    }

    if (error || !repository) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {error || 'Repository not found'}
                    </h1>
                </div>
            </Layout>
        )
    }

    const tabs = [
        {
            name: 'Features',
            component: AllFeatureSettings,
            props: {
                settings,
                onUpdate: updateSettings,
                repository,
                plan
            }
        },
        {
            name: 'Activity Log',
            component: ActivityLog,
            props: { activities, loading: activitiesLoading }
        },
        {
            name: 'Feedback Inbox',
            component: FeedbackInbox,
            props: {}
        },
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{repository.name.split('/').pop()}</h1>
                            <p className="mt-1 text-sm text-gray-600">
                                Configure EchoGuard settings for this repository
                            </p>
                        </div>
                        <div className="flex items-center space-x-4">
             <span className="text-sm font-medium text-gray-700">
               {isEnabled ? 'EchoGuard Enabled' : 'EchoGuard Disabled'}
             </span>
                            <Switch
                                checked={isEnabled}
                                onChange={handleToggleEnabled}
                                className={`${
                                    isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                            >
               <span
                   className={`${
                       isEnabled ? 'translate-x-6' : 'translate-x-1'
                   } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
               />
                            </Switch>
                        </div>
                    </div>
                </div>

                <Tab.Group>
                    <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.name}
                                className={({ selected }) =>
                                    classNames(
                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700',
                                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                        selected
                                            ? 'bg-white shadow'
                                            : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                                    )
                                }
                            >
                                {tab.name}
                            </Tab>
                        ))}
                    </Tab.List>

                    <Tab.Panels className="mt-6">
                        {tabs.map((tab, idx) => (
                            <Tab.Panel key={idx} className="bg-white rounded-lg p-6 border border-gray-200">
                                <tab.component {...tab.props} />
                            </Tab.Panel>
                        ))}
                    </Tab.Panels>
                </Tab.Group>
            </div>
        </Layout>
    )
}
